import { TFunction } from "i18next";
import {
  INotification,
  NotificationAssetType,
  NotificationCTAType,
} from "models/notificationItem.models";
import { NotificationTemplateType } from "utils/constants/notifications.constants";
import { ProjectStatus } from "utils/constants/project.constants";
import {
  getDisplayedAssetStatus,
  getDisplayedProjectStatus,
  TPossibleAssetStatus,
} from "utils/helpers/getDisplayedStatus";
import { FolderIcon, GlobeLocatorIcon } from "assets/icons";

export const getReadableAssetType = (
  assetType: NotificationAssetType,
  t: TFunction,
) => {
  const assetTypeMap: { [key in NotificationAssetType]: string } = {
    [NotificationAssetType.Document]: t(
      "navigation.notifications.notificationAssetTypes.document",
    ),
    [NotificationAssetType.SupportingInformation]: t(
      "navigation.notifications.notificationAssetTypes.supportingInformation",
    ),
    [NotificationAssetType.Comment]: t(
      "navigation.notifications.notificationAssetTypes.comment",
    ),
    [NotificationAssetType.Question]: t(
      "navigation.notifications.notificationAssetTypes.question",
    ),
    [NotificationAssetType.QuestionResponse]: t(
      "navigation.notifications.notificationAssetTypes.questionResponse",
    ),
    [NotificationAssetType.Product]: t(
      "navigation.notifications.notificationAssetTypes.product",
    ),
    [NotificationAssetType.ProductFamily]: t(
      "navigation.notifications.notificationAssetTypes.productFamily",
    ),
    [NotificationAssetType.Substance]: t(
      "navigation.notifications.notificationAssetTypes.substance",
    ),
    [NotificationAssetType.AssessmentReport]: t(
      "navigation.notifications.notificationAssetTypes.assessmentReport",
    ),
    [NotificationAssetType.ImportedHAQ]: t(
      "navigation.notifications.notificationAssetTypes.questions",
    ),
    [NotificationAssetType.HADecisionLetter]: t(
      "navigation.notifications.notificationAssetTypes.HADecisionLetter",
    ),
    [NotificationAssetType.ECTD]: t(
      "navigation.notifications.notificationAssetTypes.ECTD",
    ),
    [NotificationAssetType.ApplicantsStrategy]: t(
      "navigation.notifications.notificationAssetTypes.applicantsStrategy",
    ),
    [NotificationAssetType.CoverLetter]: t(
      "navigation.notifications.notificationAssetTypes.coverLetter",
    ),
    [NotificationAssetType.InspectionReports]: t(
      "navigation.notifications.notificationAssetTypes.inspectionReports",
    ),
    [NotificationAssetType.ReferenceDocument]: t(
      "navigation.notifications.notificationAssetTypes.referenceDocument",
    ),
    [NotificationAssetType.StructuredDocument]: t(
      "navigation.notifications.notificationAssetTypes.structuredDocument",
    ),
    [NotificationAssetType.StructuredDocumentChild]: t(
      "navigation.notifications.notificationAssetTypes.structuredDocument",
    ),
  };

  return assetTypeMap[assetType] || assetType;
};

export const getNotificationIcon = (type: NotificationCTAType) => {
  // More notification icon types will be supported in the future
  switch (type) {
    case NotificationCTAType.Task:
      return <GlobeLocatorIcon data-testid="notification-task-icon" />;
    default:
      return <FolderIcon data-testid="notification-icon" />;
  }
};

export const getNotificationTitleForReason = (
  { templateType, metadata }: INotification,
  t: TFunction,
) => {
  if (
    templateType ===
      NotificationTemplateType.ProjectRelianceStatusUpdateWithStateAndReason &&
    metadata.targetStatus === ProjectStatus.Cancelled
  ) {
    return t("navigation.notifications.reasonForCancellation");
  }

  if (
    templateType ===
    NotificationTemplateType.ProjectRelianceStatusUpdateWithStateAndReasonRevert
  ) {
    return t("navigation.notifications.reasonForReversion");
  }

  return t("navigation.notifications.reason");
};

export const getDisplayedNotificationStatus = (
  type: NotificationCTAType,
  status: ProjectStatus | TPossibleAssetStatus,
  t: TFunction,
) => {
  return type === NotificationCTAType.Project
    ? getDisplayedProjectStatus(status as ProjectStatus, t)
    : getDisplayedAssetStatus(status as TPossibleAssetStatus, t);
};
